import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Upload, Eye, Trash2, FileText, AlertTriangle, AlertOctagon, CalendarCheck, CheckCircle2, PartyPopper, Undo2, Check, Lock } from "lucide-react";
import { toast } from "sonner";
import DocViewerModal from "./DocViewerModal";

import { daysUntil, fmtDay, todayISO } from "../lib/dates";

/**
 * EmojiBurst — pure-CSS celebration overlay. When `active` flips true it renders
 * 24 emojis that float up with randomized rotation + horizontal drift. Auto
 * unmounts after the 1.6s keyframe cycle completes.
 */
function EmojiBurst({ active, emojis = ["🎉","✨","🎊","🥳","💸","✅","💰","🙌"] }) {
  if (!active) return null;
  const items = Array.from({ length: 24 }, (_, i) => {
    const left = Math.random() * 100;
    const delay = Math.random() * 0.4;
    const dur = 1.1 + Math.random() * 0.6;
    const drift = (Math.random() - 0.5) * 160;
    const rot = (Math.random() - 0.5) * 540;
    const emoji = emojis[i % emojis.length];
    return (
      <span
        key={i}
        className="absolute bottom-2 text-2xl select-none pointer-events-none"
        style={{
          left: `${left}%`,
          animation: `edc-burst ${dur}s ease-out ${delay}s forwards`,
          "--edc-drift": `${drift}px`,
          "--edc-rot": `${rot}deg`,
        }}
      >
        {emoji}
      </span>
    );
  });
  return (
    <>
      <style>{`
        @keyframes edc-burst {
          0%   { transform: translate(0, 0) rotate(0); opacity: 0; }
          15%  { opacity: 1; }
          100% { transform: translate(var(--edc-drift,0), -220px) rotate(var(--edc-rot,0)); opacity: 0; }
        }
      `}</style>
      <div data-testid="expiry-doc-burst" className="absolute inset-0 overflow-hidden pointer-events-none">
        {items}
      </div>
    </>
  );
}

const RING = {
  done: "#059669", expired: "#e11d48", urgent: "#e11d48", warn: "#f59e0b", ok: "#10b981", neutral: "#94a3b8",
};

function CountdownRing({ days, level, complete }) {
  const r = 26, c = 2 * Math.PI * r;
  const pct = complete ? 1 : days == null ? 0 : Math.max(0.04, Math.min(1, days / 90));
  const hot = !complete && (level === "urgent" || level === "expired");
  return (
    <div data-testid="expiry-countdown-ring" className={`relative w-14 h-14 shrink-0 ${hot ? "edc-wobble" : ""}`}>
      <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
        <circle cx="32" cy="32" r={r} fill="white" stroke="#e2e8f0" strokeWidth="6" />
        <circle cx="32" cy="32" r={r} fill="none" stroke={RING[level]} strokeWidth="6" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct)} style={{ transition: "stroke-dashoffset 900ms ease-out" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none" style={{ color: RING[level] }}>
        {complete ? <Check className="w-6 h-6" strokeWidth={3} />
          : days == null ? <span className="text-lg font-bold">?</span>
          : days < 0 ? <><span className="text-sm font-extrabold">{Math.abs(days)}</span><span className="text-[8px] font-semibold uppercase">late</span></>
          : <><span className="text-lg font-extrabold heading-font">{days}</span><span className="text-[8px] font-semibold uppercase">{days === 1 ? "day" : "days"}</span></>}
      </div>
    </div>
  );
}

const FUN_THEMES = {
  done:    "bg-gradient-to-br from-emerald-50 via-white to-teal-50 border-emerald-400 text-emerald-900",
  expired: "bg-gradient-to-br from-rose-100 via-white to-orange-50 border-rose-400 text-rose-900",
  urgent:  "bg-gradient-to-br from-rose-50 via-white to-orange-50 border-rose-300 text-rose-900",
  warn:    "bg-gradient-to-br from-amber-50 via-white to-yellow-50 border-amber-300 text-amber-900",
  ok:      "bg-gradient-to-br from-emerald-50 via-white to-sky-50 border-emerald-200 text-emerald-900",
  neutral: "bg-gradient-to-br from-slate-50 via-white to-indigo-50 border-slate-200 text-slate-800",
};
const MOODS = { done: "Nailed it!", expired: "Overdue — act now", urgent: "Hurry up!", warn: "Coming up soon", ok: "All good, plenty of time", neutral: "Set the date to start the countdown" };
const FUN_BADGE = { done: "On time", expired: "Expired", urgent: "Urgent", warn: "Soon", ok: "On track", neutral: "Not set" };

function Highlights({ items }) {
  if (!items?.length) return null;
  return (
    <div data-testid="expiry-single-meta" className="flex items-center gap-1.5 min-w-0 whitespace-nowrap overflow-hidden">
      {items.map((h, i) => {
        const I = h.icon;
        return (
          <span key={i} data-testid={`expiry-highlight-${h.key}`} title={`${h.label}: ${h.value}`}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${h.grow ? "min-w-0 shrink" : "shrink-0"} ${h.className}`}>
            {I && <I className="w-3 h-3 shrink-0" />}<span className="truncate">{h.value}</span>
          </span>
        );
      })}
    </div>
  );
}

/**
 * Compact TWO-column card that combines the expiry banner (left) with the
 * date + document controls (right). Also supports an optional "Mark complete"
 * button driven by `completionField`: when the flag is true the whole card
 * flips to a green celebratory state and the edit controls are hidden.
 *
 * Completion props:
 *   completionField        — Mongo field holding the bool (e.g. "deposit_paid")
 *   completionDateField    — Mongo field holding the completion date (ISO)
 *   completionLabel        — button text (e.g. "Deposit Paid")
 *   completionDoneMessage  — headline when complete (e.g. "Deposit Paid")
 *   completionSubMessage   — sub-copy under the headline
 *   initialComplete / initialCompleteDate — current server state
 */
export default function ExpiryDocCard({
  applicationId, uploadPath, downloadPath, docType = "Expiry Document",
  dateField = "visa_expiry", dateLabel = "Expiry",
  expiryLabel = "Current Expiry",
  fileIdField = "expiry_doc_file_id",
  fileNameField = "expiry_doc_file_name",
  showDocument = true,
  contextChips = [],
  initialDate, initialFileId, initialFileName,
  // Completion toggle (optional)
  completionField, completionDateField, completionLabel,
  completionDoneMessage, completionSubMessage,
  // When true, clicking the completion button opens a modal that requires a
  // receipt / screenshot upload before the flag is saved. The file reference
  // is persisted via the existing fileIdField / fileNameField.
  completionRequiresFile = false,
  completionFilePrompt = "Upload the receipt or screenshot to confirm.",
  initialComplete = false, initialCompleteDate = null,
  onSaved,
  beforeComplete,
  completeLockedReason, undoLockedReason,
  variant = "default", highlights = [],
}) {
  const fun = variant === "fun";
  const [dateVal, setDateVal] = useState(initialDate || "");
  const [fileId, setFileId] = useState(initialFileId || null);
  const [fileName, setFileName] = useState(initialFileName || "");
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [complete, setComplete] = useState(!!initialComplete);
  const [completeDate, setCompleteDate] = useState(initialCompleteDate || null);
  const [burstKey, setBurstKey] = useState(0);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [savingComplete, setSavingComplete] = useState(false);
  const receiptRef = useRef();
  const extraPatchRef = useRef({});
  const inputRef = useRef();

  useEffect(() => {
    setDateVal(initialDate || "");
    setFileId(initialFileId || null);
    setFileName(initialFileName || "");
    setComplete(!!initialComplete);
    setCompleteDate(initialCompleteDate || null);
  }, [initialDate, initialFileId, initialFileName, initialComplete, initialCompleteDate]);

  const days = daysUntil(dateVal);
  // When the card is in "complete" state, lock the theme to green regardless of date.
  const level = complete ? "done"
    : days == null ? "neutral"
    : days < 0 ? "expired"
    : days <= 14 ? "urgent"
    : days <= 30 ? "warn"
    : "ok";
  const themes = {
    done:    "bg-emerald-50 border-emerald-400 text-emerald-900 ring-2 ring-emerald-200",
    expired: "bg-rose-50 border-rose-300 text-rose-900",
    urgent:  "bg-rose-50 border-rose-300 text-rose-900",
    warn:    "bg-amber-50 border-amber-300 text-amber-900",
    ok:      "bg-emerald-50 border-emerald-200 text-emerald-900",
    neutral: "bg-slate-50 border-slate-200 text-slate-800",
  };
  const badgeText = complete ? "DONE ON TIME"
    : days == null ? "No expiry set"
    : days < 0 ? `EXPIRED ${Math.abs(days)}d ago`
    : days === 0 ? "EXPIRES TODAY"
    : days === 1 ? "1 day left"
    : `${days} days left`;
  const badgeCls = complete ? "bg-emerald-600 text-white"
    : level === "urgent" || level === "expired" ? "bg-rose-600 text-white"
    : level === "warn" ? "bg-amber-500 text-white"
    : level === "ok" ? "bg-emerald-600 text-white"
    : "bg-slate-300 text-slate-800";
  const Icon = complete ? CheckCircle2
    : level === "urgent" || level === "expired" ? AlertOctagon
    : level === "warn" ? AlertTriangle
    : CalendarCheck;
  const iconTone = complete ? "text-emerald-600"
    : level === "urgent" || level === "expired" ? "text-rose-600"
    : level === "warn" ? "text-amber-600"
    : level === "ok" ? "text-emerald-600"
    : "text-slate-500";
  const isoDate = dateVal ? fmtDay(dateVal) : "Not set";
  const completeDateFmt = completeDate ? fmtDay(completeDate) : "";

  const saveDate = async (v) => {
    const clean = v || null;
    if (clean === (initialDate || null)) return;
    try {
      await api.patch(uploadPath.replace("/upload", ""), { [dateField]: clean });
      onSaved?.({ [dateField]: clean });
      toast.success(clean ? "Date saved" : "Date cleared");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const toggleComplete = async (next, uploadedFile = null) => {
    if (!completionField) return;
    const today = todayISO();
    const patch = { [completionField]: next, ...(next ? extraPatchRef.current : {}) };
    if (completionDateField) patch[completionDateField] = next ? today : null;
    // If a receipt was just uploaded as part of this completion, persist its reference too.
    if (next && uploadedFile) {
      patch[fileIdField] = uploadedFile.id;
      patch[fileNameField] = uploadedFile.name;
    }
    // On undo, clear any attached receipt so the next cycle starts fresh.
    if (!next && completionRequiresFile) {
      patch[fileIdField] = null;
      patch[fileNameField] = null;
    }
    setSavingComplete(true);
    try {
      const res = await api.patch(uploadPath.replace("/upload", ""), patch);
      setComplete(next);
      setCompleteDate(next ? today : null);
      if (next && uploadedFile) { setFileId(uploadedFile.id); setFileName(uploadedFile.name); }
      if (!next && completionRequiresFile) { setFileId(null); setFileName(""); }
      onSaved?.(patch);
      if (next) {
        setBurstKey(k => k + 1);
        setTimeout(() => setBurstKey(0), 1800);
        const bo = res.data?.backed_out;
        toast.success(`${completionDoneMessage || completionLabel}! 🎉${bo ? ` · ${bo} other application(s) closed` : ""}`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSavingComplete(false); }
  };

  // Flow: button-click → if receipt required, open modal → user picks file → upload → toggleComplete(true, {id,name})
  const requestComplete = async () => {
    const extra = beforeComplete ? await beforeComplete() : {};
    if (extra === null) return;
    extraPatchRef.current = extra;
    if (!completionRequiresFile) return toggleComplete(true);
    setReceiptFile(null);
    setReceiptOpen(true);
  };

  const confirmCompleteWithReceipt = async () => {
    if (!receiptFile) { toast.error("Please choose the receipt or screenshot"); return; }
    if (receiptFile.size > 15 * 1024 * 1024) { toast.error("Max 15 MB"); return; }
    setSavingComplete(true);
    try {
      const fd = new FormData();
      fd.append("file", receiptFile);
      const r = await api.post(`${uploadPath}?doc_type=${encodeURIComponent(docType)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newId = r.data.id || r.data.file_id || r.data._id;
      const newName = r.data.original_filename || receiptFile.name;
      setReceiptOpen(false);
      await toggleComplete(true, { id: newId, name: newName });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setSavingComplete(false);
      setReceiptFile(null);
      if (receiptRef.current) receiptRef.current.value = "";
    }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("Max 15 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`${uploadPath}?doc_type=${encodeURIComponent(docType)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newId = r.data.id || r.data.file_id || r.data._id;
      const newName = r.data.original_filename || f.name;
      await api.patch(uploadPath.replace("/upload", ""), { [fileIdField]: newId, [fileNameField]: newName });
      setFileId(newId); setFileName(newName);
      onSaved?.({ [fileIdField]: newId, [fileNameField]: newName });
      toast.success("Uploaded");
    } catch (er) { toast.error(er.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const clearFile = async () => {
    try {
      await api.patch(uploadPath.replace("/upload", ""), { [fileIdField]: null, [fileNameField]: null });
      setFileId(null); setFileName("");
      onSaved?.({ [fileIdField]: null, [fileNameField]: null });
      toast.success("Removed");
      setConfirmRemove(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const viewUpload = async () => {
    try {
      const r = await api.get(`${downloadPath}/${fileId}/download`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(r.data);
      setViewer({ url: blobUrl, title: fileName || "Expiry document" });
    } catch (e) { toast.error("Could not open"); }
  };

  return (
    <div data-testid="expiry-doc-card" className={`relative overflow-hidden rounded-xl border-2 ${fun ? FUN_THEMES[level] : themes[level]} p-3 transition-all ${fun ? "h-full" : ""}`}>
      <EmojiBurst key={burstKey} active={burstKey > 0} />
      {fun && <style>{`@keyframes edc-wob{0%,100%{transform:rotate(0)}25%{transform:rotate(-6deg)}75%{transform:rotate(6deg)}}.edc-wobble{animation:edc-wob 1.4s ease-in-out infinite}`}</style>}

      {fun ? (
        <div data-testid="expiry-fun-body" className="relative flex flex-col gap-2 h-full">
          <div className="flex items-center gap-3 min-w-0">
            <CountdownRing days={days} level={level} complete={complete} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 min-w-0">
                <span data-testid="expiry-label" className="text-[10px] uppercase tracking-widest opacity-70 font-bold truncate">{expiryLabel}</span>
                <span data-testid="expiry-badge" className={`shrink-0 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${badgeCls} ${level === "urgent" ? "animate-pulse" : ""}`}>{complete && dateVal && completeDate && completeDate.slice(0, 10) > dateVal ? "Late" : FUN_BADGE[level]}</span>
              </div>
              <div data-testid="expiry-title" className="text-lg heading-font font-bold leading-tight truncate" title={complete ? completionDoneMessage : isoDate}>
                {complete ? (completionDoneMessage || "Complete") : dateVal ? isoDate : "No date yet"}
              </div>
              <div className="text-[11px] font-medium opacity-80 truncate">{complete ? `Marked on ${completeDateFmt || "today"}` : MOODS[level]}</div>
            </div>
            {complete && (
              <div className="flex items-center gap-1 shrink-0">
                {fileId && (
                  <Button size="sm" variant="outline" data-testid="expiry-doc-view-file" onClick={viewUpload} className="h-7 px-2 bg-white/90 text-xs">
                    <Eye className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">{showDocument ? "View" : "Receipt"}</span>
                  </Button>
                )}
                <Button size="sm" variant="ghost" data-testid="expiry-doc-undo-complete" onClick={() => undoLockedReason ? toast.error(undoLockedReason) : toggleComplete(false)} disabled={savingComplete} title={undoLockedReason || "Undo"}
                  className="h-7 px-2 text-xs text-slate-600 hover:text-rose-700 hover:bg-rose-50">
                  <Undo2 className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">Undo</span>
                </Button>
              </div>
            )}
          </div>
          <Highlights items={highlights} />
          {complete ? (
            <div data-testid="expiry-done-strip" className="mt-auto flex items-center gap-1.5 rounded-lg bg-white/70 border border-emerald-200 px-2.5 py-1.5 text-[11px] text-emerald-800 min-w-0">
              <PartyPopper className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{completionSubMessage || `${completionLabel || "Done"} before the deadline`}{dateVal ? ` · ${dateLabel} ${isoDate}` : ""}</span>
            </div>
          ) : (
            <>
            <div className="mt-auto flex flex-wrap items-center gap-1.5 rounded-lg bg-white/75 border border-slate-200/80 p-1.5">
              <Input type="date" data-testid="expiry-doc-date-input" title={dateLabel} value={dateVal || ""}
                onChange={e => setDateVal(e.target.value)} onBlur={e => saveDate(e.target.value)}
                className="h-8 w-[138px] text-xs bg-white" />
              {showDocument && (fileId ? (
                <span className="inline-flex items-center gap-1 h-8 min-w-0 max-w-[160px] rounded-md border border-emerald-200 bg-emerald-50 px-2 text-xs text-emerald-800">
                  <FileText className="w-3.5 h-3.5 shrink-0" />
                  <button data-testid="expiry-doc-view-file" onClick={viewUpload} className="truncate hover:underline" title={fileName}>{fileName || "Document"}</button>
                  <button data-testid="expiry-doc-remove-file" onClick={() => setConfirmRemove(true)} className="shrink-0 text-rose-500 hover:text-rose-700"><Trash2 className="w-3.5 h-3.5" /></button>
                </span>
              ) : (
                <>
                  <input ref={inputRef} data-testid="expiry-doc-upload-input" type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={upload} />
                  <Button size="sm" variant="outline" data-testid="expiry-doc-upload-btn" onClick={() => inputRef.current?.click()} disabled={uploading} className="h-8 px-2 text-xs bg-white">
                    <Upload className="w-3.5 h-3.5 mr-1" />{uploading ? "Uploading…" : "Upload"}
                  </Button>
                </>
              ))}
              {completionField && (
                <span className="flex-1 min-w-[120px]" title={completeLockedReason || ""}>
                <Button size="sm" data-testid="expiry-doc-mark-complete" onClick={requestComplete} disabled={savingComplete || !!completeLockedReason}
                  className="h-8 w-full text-xs bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98] text-white font-semibold shadow-sm transition-transform disabled:bg-slate-300 disabled:text-slate-600 disabled:opacity-100">
                  {completeLockedReason ? <Lock className="w-3.5 h-3.5 mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}{completionLabel || "Mark Complete"}
                </Button>
                </span>
              )}
            </div>
            {completeLockedReason && <div data-testid="expiry-doc-locked-hint" className="-mt-1 text-[11px] text-slate-500 flex items-center gap-1"><Lock className="w-3 h-3" />{completeLockedReason}</div>}
            </>
          )}
        </div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-center relative">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`p-2 rounded-lg bg-white/60 ${iconTone} shrink-0`}><Icon className="w-6 h-6" /></div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest opacity-70 font-semibold">{expiryLabel}</div>
            {complete ? (
              <>
                <div className="text-base md:text-lg heading-font font-bold leading-tight whitespace-nowrap overflow-hidden text-ellipsis flex items-center gap-2">
                  <span className="truncate">{completionDoneMessage || "Complete"}</span>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${badgeCls} shrink-0`}>{badgeText}</span>
                </div>
                <div className="mt-1 text-xs font-medium flex items-center gap-1 text-emerald-700">
                  <PartyPopper className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{completeDateFmt || "Just now"}</span>
                </div>
              </>
            ) : (
              <>
                <div className="text-xl heading-font font-bold leading-tight flex flex-wrap items-center gap-2">
                  <span>{isoDate}</span>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${badgeCls}`}>{badgeText}</span>
                </div>
                {contextChips.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5 text-[11px]">
                    {contextChips.map((c, i) => c && (
                      <span key={i} className="px-1.5 py-0.5 rounded-full bg-white/70 border border-current/10">
                        {c.label ? <><span className="opacity-60">{c.label}:</span> <span className="font-medium">{c.value}</span></> : c}
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* RIGHT — Controls */}
        <div className="bg-white/70 rounded-lg p-2.5 border border-current/10 space-y-2">
          {complete ? (
            <div className="flex items-center justify-end gap-2">
              {fileId && (
                <Button size="sm" variant="outline" data-testid="expiry-doc-view-file" onClick={viewUpload} className="h-7 px-2 bg-white">
                  <Eye className="w-3.5 h-3.5 mr-1" />Receipt
                </Button>
              )}
              {completionField && (
                <Button
                  size="sm" variant="ghost"
                  data-testid="expiry-doc-undo-complete"
                  onClick={() => toggleComplete(false)}
                  disabled={savingComplete}
                  className="h-7 px-2 text-emerald-700 hover:bg-emerald-100"
                  title="Undo"
                >
                  <Undo2 className="w-3.5 h-3.5 mr-1" />Undo
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <span className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold min-w-[68px]">{dateLabel}</span>
                <Input
                  type="date"
                  data-testid="expiry-doc-date-input"
                  value={dateVal || ""}
                  onChange={e=>setDateVal(e.target.value)}
                  onBlur={e=>saveDate(e.target.value)}
                  className="h-8 text-sm flex-1 bg-white"
                />
              </div>
              {showDocument && (
                <div className="flex items-center gap-2">
                  <span className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold min-w-[68px]">Document</span>
                  {fileId ? (
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                      <FileText className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                      <span className="text-xs text-slate-800 flex-1 truncate" title={fileName}>{fileName || "Uploaded file"}</span>
                      <Button size="sm" variant="outline" data-testid="expiry-doc-view-file" onClick={viewUpload} className="h-7 px-2 bg-white">
                        <Eye className="w-3.5 h-3.5 mr-1" />View
                      </Button>
                      <Button size="sm" variant="outline" data-testid="expiry-doc-remove-file" onClick={()=>setConfirmRemove(true)} className="h-7 px-2 bg-white text-rose-600 border-rose-200 hover:bg-rose-50">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <input ref={inputRef} data-testid="expiry-doc-upload-input" type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={upload} />
                      <Button size="sm" variant="outline" data-testid="expiry-doc-upload-btn" onClick={()=>inputRef.current?.click()} disabled={uploading} className="h-7 px-2 flex-1 justify-start bg-white">
                        <Upload className="w-3.5 h-3.5 mr-1" />{uploading ? "Uploading…" : "Upload PDF / Image"}
                      </Button>
                    </>
                  )}
                </div>
              )}
              {completionField && (
                <Button
                  size="sm"
                  data-testid="expiry-doc-mark-complete"
                  onClick={requestComplete}
                  disabled={savingComplete}
                  className="h-8 w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-sm"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1.5" />{completionLabel || "Mark Complete"}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      )}

      <Dialog open={receiptOpen} onOpenChange={(v)=>{ if (!savingComplete) setReceiptOpen(v); }}>
        <DialogContent className="bg-white max-w-md" data-testid="expiry-doc-receipt-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-5 h-5" />{completionLabel || "Confirm"}
            </DialogTitle>
            <DialogDescription className="pt-1">{completionFilePrompt}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <input
              ref={receiptRef}
              type="file"
              data-testid="expiry-doc-receipt-input"
              accept=".pdf,.jpg,.jpeg,.png"
              className="hidden"
              onChange={e => setReceiptFile(e.target.files?.[0] || null)}
            />
            <div className="flex items-center gap-2 min-w-0">
              <Button type="button" variant="outline" onClick={() => receiptRef.current?.click()} className="h-9 shrink-0">
                <Upload className="w-4 h-4 mr-1.5" />{receiptFile ? "Change file" : "Choose file"}
              </Button>
              <div data-testid="expiry-doc-receipt-filename" className="text-xs text-slate-600 truncate flex-1 min-w-0" title={receiptFile?.name}>
                {receiptFile ? receiptFile.name : <span className="text-slate-400">No file selected</span>}
              </div>
            </div>
            <div className="text-[11px] text-slate-500">PDF or image up to 15 MB. Required.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiptOpen(false)} disabled={savingComplete}>Cancel</Button>
            <Button
              data-testid="expiry-doc-receipt-confirm"
              onClick={confirmCompleteWithReceipt}
              disabled={!receiptFile || savingComplete}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {savingComplete ? "Saving…" : `Confirm ${completionLabel || ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DocViewerModal open={!!viewer} onOpenChange={(v)=>{ if (!v) { if (viewer?.url?.startsWith("blob:")) URL.revokeObjectURL(viewer.url); setViewer(null); } }} url={viewer?.url} title={viewer?.title} />

      <Dialog open={confirmRemove} onOpenChange={setConfirmRemove}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700"><AlertTriangle className="w-5 h-5" />Remove document?</DialogTitle>
            <DialogDescription className="pt-1">
              This will unlink <span className="font-medium text-slate-900">{fileName || "the uploaded file"}</span> from this file. The file itself will remain in the Documents tab and can be re-attached later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setConfirmRemove(false)}>Cancel</Button>
            <Button data-testid="expiry-doc-remove-confirm" onClick={clearFile} className="bg-rose-600 hover:bg-rose-700 text-white">Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
